import {Apollo, ApolloModule} from 'apollo-angular';
import {HttpLink} from 'apollo-angular/http';
import {ApolloLink, concat, InMemoryCache} from '@apollo/client/core';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationStart, NavigationEnd, RouteReuseStrategy, NavigationCancel } from '@angular/router';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { routing } from './app.routing';

import { AppComponent } from './app.component';

import { FundraisingModule } from './fundraising/fundraising.module';
import { AuthenticationModule } from './authentication/authentication.module';
import { SandboxModule } from './sandbox/sandbox.module';
import { AuthenticationService } from './authentication/authentication.service';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpHeaders, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthInterceptor } from './authentication/auth.interceptor';
import { SelectModule } from 'ng-select';
import { DonationService } from './core/services/donation.service';
import { EventService } from './core/services/event.service';
import { MomentModule } from 'ngx-moment';
import { CoreModule, CustomCurrencyMaskConfig } from './core/core.module';
import { AuthGuard } from './core/guards/auth.guard';
import { UserService } from './core/services/user.service';
import { PageService } from './core/services/page.service';
import { TeamService } from './core/services/team.service';
import { UiStateService } from './core/services/ui-state.service';
import { NgxMaskModule } from 'ngx-mask';
import { CURRENCY_MASK_CONFIG, CurrencyMaskModule } from 'ng2-currency-mask';
import { DatatableService } from './core/services/datatable.service';
import { WA18396Interceptor } from './core/WA18396.interceptor';
import { ModerationInterceptor } from './core/moderation.interceptor';

import { ProgressService } from './core/services/progress.service';
import { UtilsService } from './core/services/utils.service';
import { NgbDateFRParserFormatter } from './core/NgbDateFrParserFormatter';
import { LLSRouteReuseStrategy } from './core/LLSRouteReuseStrategy';
import { Angulartics2Module } from 'angulartics2';
import { ImageUploadModule } from 'angular2-image-upload';
import { ImageService } from './core/services/image.service';

import { LLSHttpInterceptor } from './core/lls-http-interceptor';
import { UnsavedChangesService } from './core/services/unsaved-changes.service';
import { PostService } from './core/services/post.service';
import { FacebookFundraiserService } from './core/services/facebook-fundraiser.service';
import { FacebookService } from './core/services/facebook.service';
import { HybridToolsService } from './core/services/hybrid-tools.service';
import { GraphqlService } from './core/graphql/graphql.service';
import { PermissionService } from './core/services/permission.service';
import { NationalTeamsService } from './core/services/national-teams.service';
import { FundraisingViewService } from './core/services/fundraising-view.service';
import { AppDetailsService } from './core/services/app.details.service';
import { CKEditorModule } from 'ng2-ckeditor';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { Capacitor } from '@capacitor/core';

declare var $: any;
declare var jQuery: any;
declare var ENVIRONMENT_CONFIG: any;

@NgModule({ declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        FormsModule,
        AuthenticationModule,
        SandboxModule,
        FundraisingModule,
        BrowserAnimationsModule,
        NgxMaskModule.forRoot(),
        CurrencyMaskModule,
        NgbModule,
        SelectModule,
        MomentModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({positionClass: 'toast-top-center', onActivateTick: true}),
        ApolloModule,
        routing,
        CoreModule,
        Angulartics2Module.forRoot(),
        ImageUploadModule.forRoot(),
        CKEditorModule,
        ServiceWorkerModule.register('ngsw-worker.js', {enabled: environment.production && !Capacitor.isNativePlatform()})
    ],
    providers: [
        {provide: HTTP_INTERCEPTORS, useClass: LLSHttpInterceptor, multi: true},
        AuthenticationService,
        UiStateService,
        PageService,
        DatatableService,
        GraphqlService,
        DonationService,
        {
            provide: HybridToolsService,
            useClass: HybridToolsService
        },
        EventService,
        UserService,
        UnsavedChangesService,
        UtilsService,
        TeamService,
        ImageService,
        ProgressService,
        PostService,
        FacebookFundraiserService,
        FacebookService,
        PermissionService,
        NationalTeamsService,
        FundraisingViewService,
        AppDetailsService,
        {
            provide: NgbDateParserFormatter,
            useClass: NgbDateFRParserFormatter
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: WA18396Interceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ModerationInterceptor,
            multi: true
        },
        {
            provide: CURRENCY_MASK_CONFIG,
            useValue: CustomCurrencyMaskConfig
        },
        {
            provide: RouteReuseStrategy,
            useClass: LLSRouteReuseStrategy
        },
        provideHttpClient(withInterceptorsFromDi())
    ],
    bootstrap: [AppComponent]
})

export class AppModule {
    constructor(
        private router: Router,
        private progressService: ProgressService,
        private apollo: Apollo,
        private hybridToolsService: HybridToolsService,
        private httpLink: HttpLink
    ) {
        apollo.create({
            link: this.createApolloHttpLink(),
            cache: new InMemoryCache()
        });

        router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.hybridToolsService.removeMobileMenuClasses();
                window.scrollTo(0, 0);
                this.progressService.startProgress(true);
            }
            // don't stop progress before after-login finishes navigating to home page.
            // the stopProgress will be called in next cycle when navigation happens to home page
            if ((event instanceof NavigationEnd
                && event.url !== '/after-login')
                || event instanceof NavigationCancel) {
                setTimeout(() => {
                    this.progressService.stopProgress(true);
                }, 200);
            }
        });
    }

    createApolloHttpLink() {
        const http = this.httpLink.create({uri: ENVIRONMENT_CONFIG.graphqlUrl + '/graphql'});
        const authMiddleware = new ApolloLink((operation, forward) => {
            // add the authorization to the headers
            let currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
            operation.setContext({
                headers: new HttpHeaders().set('Authorization', 'Bearer ' + currentUser.accessToken || null),
                uri: ENVIRONMENT_CONFIG.graphqlUrl + '/graphql/' + operation.operationName
            });
            return forward(operation);
        });
        return concat(authMiddleware, http);
    }
}
